import React, { useRef, useState } from 'react';
import { useFrame, useLoader, useThree } from '@react-three/fiber';
import * as THREE from 'three';

const ImagePin = ({ 
  position, 
  user, 
  isCurrentUser, 
  onClick 
}) => {
  const [hovered, setHovered] = useState(false);
  const spriteRef = useRef();
  const { camera } = useThree();
  
  // Load pin image texture
  const texture = useLoader(THREE.TextureLoader, '/pin-image.png');
  
  // Pulsing animation and visibility check
  useFrame((state) => {
    if (spriteRef.current) {
      // Pulsing animation - larger scale on hover
      if (hovered) {
        const pulse = Math.sin(state.clock.elapsedTime * 3) * 0.05 + 1;
        spriteRef.current.scale.set(pulse * 0.11, pulse * 0.11, 1); // Slightly larger on hover
      } else {
        spriteRef.current.scale.set(0.09, 0.09, 1);
      }
      
      // Check if pin is facing camera (on front side of globe)
      const pinWorldPos = new THREE.Vector3(position.x, position.y - 0.08, position.z);
      const cameraDirection = new THREE.Vector3().subVectors(camera.position, pinWorldPos).normalize();
      const pinNormal = pinWorldPos.clone().normalize(); // Normal points outward from globe center
      const dotProduct = pinNormal.dot(cameraDirection);
      
      // If dot product > 0, pin is on front side; otherwise it's on back
      // Control visibility directly on the sprite instead of using state
      spriteRef.current.visible = dotProduct > 0;
    }
  });
  
  // Handle click
  const handleClick = (e) => {
    e.stopPropagation();
    const screenX = e.clientX || (window.innerWidth / 2);
    const screenY = e.clientY || (window.innerHeight / 2);
    onClick && onClick(user, { x: screenX, y: screenY });
  };
  
  // Color tint for pins - MUCH BRIGHTER yellow on hover to override red texture
  const color = hovered ? '#FFFF44' : '#ffffff'; // Very bright yellow on hover
  const emissive = hovered ? '#FFAA00' : (isCurrentUser ? '#ff0000' : '#00d4ff'); // Orange emissive on hover
  const emissiveIntensity = hovered ? 2.5 : 0.1; // Much higher intensity on hover
  
  // Adjust position to move south by 0.08 units (negative Y direction)
  const adjustedPosition = [position.x, position.y - 0.08, position.z];
  
  return (
    <sprite
      ref={spriteRef}
      position={adjustedPosition}
      scale={[0.09, 0.09, 1]}
      onPointerOver={() => {
        setHovered(true);
        document.body.style.cursor = 'pointer';
      }}
      onPointerOut={() => {
        setHovered(false);
        document.body.style.cursor = 'default';
      }}
      onClick={handleClick}
      renderOrder={isCurrentUser ? 100 : 50}
    >
      <spriteMaterial 
        map={texture} 
        transparent={true}
        color={color}
        emissive={emissive}
        emissiveIntensity={emissiveIntensity}
        opacity={hovered ? 1.0 : 0.95}
        depthTest={false}
        blending={hovered ? THREE.AdditiveBlending : THREE.NormalBlending}
      />
    </sprite>
  );
};

export default ImagePin;
